import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { hasPermission } from "../../../../utils/permissions";
import { HiDocumentText, HiPlus, HiPrinter, HiCurrencyDollar } from "react-icons/hi";
import { DataTable } from "../../../../components/modern";
import type { Column } from "../../../../components/modern";
import "./ShipForMeInvoices.scss";

interface ShipForMeInvoicesProps {
  formData: any;
  exchangeRate: number;
  id: string;
}

export const ShipForMeInvoices: React.FC<ShipForMeInvoicesProps> = ({
  formData,
  exchangeRate,
  id,
}) => {
  const { t } = useTranslation('shipforme');
  const { isRTL } = useLanguage();

  // Define table columns
  const columns: Column[] = [
    {
      key: 'id',
      title: t('table.no'),
      sortable: true,
      filterable: true,
      width: '80px',
      render: (value) => <span className="invoice-number">#{value}</span>
    },
    {
      key: 'amount',
      title: (
        <span>
          <HiCurrencyDollar style={{ marginRight: '8px' }} />
          {t('table.amountFC')}
        </span>
      ),
      sortable: true,
      filterable: true,
      width: '120px',
      render: (value) => (
        <div className="amount-display">
          <span className="amount-value">${value}</span>
        </div>
      )
    },
    {
      key: 'amountIQD',
      title: t('table.amountIQD'),
      sortable: true,
      filterable: true,
      width: '120px',
      render: (_, invoice) => (
        <div className="amount-display iqd">
          <span className="amount-value">
            {(
              invoice.amountIQD != null
                ? invoice.amountIQD
                : Math.ceil(Number(invoice.amount * exchangeRate) / 1000) * 1000
            )
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")} IQD
          </span>
        </div>
      )
    },
    {
      key: 'collectionDate',
      title: t('table.collectionDate'),
      sortable: true,
      filterable: true,
      width: '140px',
      render: (value) => (
        <span className="date-text">
          {value ? new Date(value).toLocaleDateString("en-CA") : "-"}
        </span>
      )
    },
    {
      key: 'isCollected',
      title: t('table.collected'),
      sortable: true,
      filterable: true,
      filterValue: (row) => row.isCollected ? t('common.yes') : t('common.no'),
      width: '100px',
      render: (value) => (
        <span className={`status-badge ${value ? 'collected' : 'pending'}`}>
          {value ? t('common.yes') : t('common.no')}
        </span>
      )
    },
    {
      key: 'isReceived',
      title: t('table.received'),
      sortable: true,
      filterable: true,
      filterValue: (row) => row.isReceived ? t('common.yes') : t('common.no'),
      width: '100px',
      render: (value) => (
        <span className={`status-badge ${value ? 'received' : 'pending'}`}>
          {value ? t('common.yes') : t('common.no')}
        </span>
      )
    },
    {
      key: 'externalInvoiceNumber',
      title: t('table.courierReceiptNo'),
      sortable: true,
      filterable: true,
      width: '150px',
      render: (value) => (
        <span className="receipt-number">{value || "-"}</span>
      )
    },
    {
      key: 'paymentGatewayName',
      title: t('table.paymentGateway', 'Payment Gateway'),
      sortable: true,
      filterable: true,
      width: '150px',
      render: (value, invoice) => (
        <span>{value || invoice?.paymentGateway?.name || '-'}</span>
      )
    },
    {
      key: 'actions',
      title: t('table.actions'),
      width: '80px',
      render: (_, invoice) => (
        <div className="action-buttons">
          <Link
            className="action-btn print-btn"
            to={`/shipforme/printinvoice/${invoice.id}`}
            title={t('actions.print', 'Print')}
          >
            <HiPrinter />
          </Link>
        </div>
      )
    }
  ];

  return (
    <div className={`shipforme-invoices-container ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Header Section */}
      <div className="invoices-header">
        <div className="header-left">
          <div className="section-title">
            <HiDocumentText className="title-icon" />
            <h5>{t('tabs.invoice')}</h5>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={formData.invoices || []}
        loading={false}
        searchable={true}
        filterable={true}
        exportable={true}
        refreshable={false}
        // addButton={hasPermission("Create Invoice") ? {
        //   label: t('actions.addNewInvoice'),
        //   onClick: () => window.location.href = `/invoices/create/ship/${id}`
        // } : undefined}
        emptyMessage={t('messages.noInvoices')}
        pageSize={10}
        className="shipforme-invoices-table"
      />
    </div>
  );
};
